import { CSSProperties, Dispatch, SetStateAction } from "react";
import { TInputImage } from "../services/api-clinic";

export type TConversation = "LEAD" | "PATIENT";

export type TProcedure =
  | "THIGH_LIPOSUCTION"
  | "CHIN_LIPOSUCTION"
  | "HAIR_TRANSPLANT"
  | "ARMS_LIPOSUCTION"
  | "ABDOMEN_LIPOSUCTION"
  | "BREAST_AUGMENTATION"
  | "BREAST_REDUCTION"
  | "BREAST_LIFT"
  | "BREAST_LIFT_AND_AUGMENTATION"
  | "LIP_FILLER"
  | "RHINOPLASTY"
  | "BLEPHAROPLASTY"
  | "RHYTIDECTOMY"
  | "OTOPLASTY"
  | "FOREHEAD_LIFT"
  | "CHIN_SURGERY"
  | "CHEEK_AUGMENTATION"
  | "LIPOSUCTION"
  | "TUMMY_TUCK"
  | "BODY_LIFT"
  | "BUTTOCK_AUGMENTATION"
  | "BBL"
  | "ARM_LIFT"
  | "THIGH_LIFT"
  | "BURN_REPAIR"
  | "SCAR_REVISION"
  | "HAND_SURGERY"
  | "RECONSTRUCTIVE_SURGERY"
  | "BOTOX_INJECTIONS"
  | "DERMAL_FILLERS"
  | "LASER_TREATMENTS"
  | "MICRODERMABRASION"
  | "FACE_LIFT"
  | "TUMMY_TUCK_AND_BREAST_LIFT" // Mommy makeover combo procedure
  | "TUMMY_TUCK_AND_BREAST_REDUCTION" // Mommy makeover combo procedure
  | "TUMMY_TUCK_AND_BREAST_AUGMENTATION" // Mommy makeover combo procedure
  | "LASER_HAIR_REMOVAL";

export type TView =
  | "HEAD_FRONT"
  | "HEAD_LEFT"
  | "HEAD_LEFT_45"
  | "HEAD_RIGHT"
  | "HEAD_RIGHT_45"
  | "HEAD_TOP"
  | "HEAD_BACK"
  | "HEAD_BACK_RIGHT_45"
  | "HEAD_BACK_LEFT_45"
  | "TORSO_BACK"
  | "TORSO_FRONT"
  | "TORSO_LEFT"
  | "TORSO_LEFT_45"
  | "TORSO_RIGHT"
  | "TORSO_RIGHT_45"
  | "ARM_FRONT"
  | "ARM_BACK"
  | "LEG_FRONT"
  | "LEG_BACK"
  | "BODY_FRONT"
  | "BODY_BACK"
  | "BODY_RIGHT"
  | "BODY_LEFT"
  | "HAND_FRONT"
  | "HAND_BACK"
  | "AFFECTED_AREA";

export type TUploadImage = {
  name:
    | "Front"
    | "Back"
    | "Left Oblique"
    | "Right Oblique"
    | "Left Side"
    | "Right Side";
  viewType: TView;
  isAISupported: boolean;
  viewTypeImage: string;
  file: Blob | null;
  fileUrl: string;
  presignedBeforeUrl: string;
  surgeryStage: TSurgeryStageType | null;
};

export type TTransformedItem = {
  procedure_view: TView;
  procedure_types: TProcedure[];
  procedure_icon: string;
  presigned_after_urls: string[];
  downloaded_after_urls: string[];
  downloaded_meta_data: {
    success: boolean;
    reason: string | null;
    implantSize: number | null;
  };
  meta_data: string;
  image_url: string;
};

export type TUpload =
  // new
  | "ImplantSizeEstimateForUser"
  | "BeforeAfterImageMetadata"
  | "SideBySideTemplateJson"
  | "BeforeAfterImageFixModeMetadata"
  | "SideBySideImageAsset"
  // old
  | "PatientImageForUser"
  | "PatientImageForForm"
  | "TransformImageForUser"
  | "TransformImageForForm";

export type TSimulationRow = {
  id: string;
  created_at: string;
  updated_at: string;
  procedure_types: TProcedure[];
  upload_type: TUpload;
};

export type TTabName =
  | "Breast"
  | "Body"
  | "Face"
  | "Head"
  | "Mommy Makeover"
  | "Profile"
  | "Images"
  | "Uploads"
  | "Gallery"
  | "Appearance"
  | "Form Format"
  | "Help"
  | "Notes"
  | "Deploy";

export type TSex = "Male" | "Female" | "Other";

export type TAreaInterest = "Face" | "Breast" | "Body";

export type TRole = "CLINIC_ADMIN" | "CLINIC_STAFF" | "PATIENT";

export type TPlan = "ENTRY" | "CORE" | "PREMIUM" | "FREE";

export type TPrimaryGoal =
  | "lead_capture"
  | "in_office_simulations"
  | "clinical_documentation"
  | "before_after_marketing"
  | "other";

export type THomeTab =
  | "Simulate"
  | "Patients"
  | "Upgrade"
  | "Analytics"
  | "Deploy Lead Gen"
  | "Before-Afters"
  | "Gallery"
  | "Image Tools"
  | "Leads"
  | "Messages"
  | "Conversations"
  | "Profile"
  | "Appointments"
  | "Consults"
  | "Key Management";

export type TUserInfo = {
  role: string;
  planType: string;
  clinicId: string;
  sdkKeys: string[];
  clinicName: string;
  displayName: string;
  completedSetup: boolean;
  showOnboardTipsBeforeAfter: boolean;
};

export type TSession = {
  role: string | null;
  clinicId: string | null;
  clinicName: string | null;
  accessToken: string;
  userId: string;
  userEmail: string;
  planType: string;
  logoUrl: string;
  logoUrlRectangle: string;
  clinicSdkKeys: string[];
  deployStatus: boolean;
  surgeons: {
    name: string;
    address: string;
    photoUrl: string;
  }[];
  showOnboardTipsBeforeAfter: boolean;
  inviteCode: string;
  primaryGoal: TPrimaryGoal | null;
  websiteUrl: string;
  createdAt: string; // From Supabase
  freeTrialEndDate?: string | null;
  isPaymentDelinquent?: boolean;
};

export type TOnboardingModalState = {
  visible: boolean;
  type: TModal;
};

export type TSessionContext = {
  session: TSession | null;
  setSession: React.Dispatch<React.SetStateAction<TSession | null>>;
  onboardingModal: TOnboardingModalState;
  setOnboardingModal: React.Dispatch<
    React.SetStateAction<TOnboardingModalState>
  >;
};

export type TSimulationLinkAndPin = {
  simulationLink: string;
  pin: string;
};

export type TModal =
  | "AddANewPatient"
  | "AddANewProcedure"
  | "ShareEngageLink"
  | "ShareReportPdf"
  | "FillOutClinicInfo"
  | "InviteAMember"
  | "ConfirmUpdatePlan"
  | "OnboardingWelcome"
  | "OnboardingDeployWidget"
  | "OnboardingSimulations"
  | "OnboardingBeforeAfter"
  | "OnboardingFutureGrowth"
  | "OnboardingConsult"
  | "UpgradePrompt"
  | "DeployWidgetNudge"
  | "ClinicEmailVerificationNudge"
  | "";

export type TFormName = "onboarding-tier1";

export type BeforeAfterImageSettingsT = {
  is_remove_face: boolean;
  is_cover_tattoo: boolean;
  is_remove_background: boolean;
  is_cover_private_parts: boolean;
  beforeImages: Blob[];
  afterImages: Blob[];
  concat_image_presigned_urls: string[];
  predefined_input_images_pairs: {
    url: string;
    label: string;
  }[];
  batch_id: string;
  metadata: {
    success: boolean;
    all_pairs: { before: TInputImage; after: TInputImage }[];
    predefined_pairs: { before: TInputImage; after: TInputImage }[];
    high_confidence_pairs: { before: TInputImage; after: TInputImage }[];
    low_confidence_pairs: { before: TInputImage; after: TInputImage }[];
    paired_with_orientation: any[];
    unpaired_before_images: TInputImage[];
    unpaired_after_images: TInputImage[];
    errors: string | null;
  } | null;
};

export type TTemplate = {
  outerContainer: CSSProperties;
  beforeImage: {
    position: {
      x: number;
      y: number;
    };
    size: {
      height: string;
      width: string;
    };
    containerStyle: CSSProperties;
    imageStyle: CSSProperties;
  };
  afterImage: {
    position: {
      x: number;
      y: number;
    };
    size: {
      height: string;
      width: string;
    };
    containerStyle: CSSProperties;
    imageStyle: CSSProperties;
  };
  images: {
    src: string;
    assetId?: number;
    preserveAspectRatio?: boolean;
    kind?: "censorship";
    censorshipSide?: "before" | "after";
    regionKey?: string;
    isUserAdjusted?: boolean;
    position: {
      x: number;
      y: number;
    };
    size: {
      height: string | number;
      width: string | number;
    };
    containerStyle: CSSProperties;
    imageStyle: CSSProperties;
  }[];
  texts: {
    content: string;
    position: {
      x: number;
      y: number;
    };
    containerStyle: CSSProperties;
    textStyle: CSSProperties;
  }[];
  divs: CSSProperties[];
};

export type TTemplateInfo = {
  isPublic: boolean;
  templateId: number | string;
  template: TTemplate;
  templateName: string;
  templateDisplayName: string;
  isDefault: boolean;
};

export type TTemplateContext = [
  template: TTemplateInfo | null,
  setTemplate: Dispatch<SetStateAction<TTemplateInfo | null>>,
];

export type TBeforeAfterProcessing = "PROGRESS_PHOTO";
export type TMessageSender = "CLINIC" | "PATIENT";

export type TIconElement = JSX.Element;

export type TCropCoords = {
  cropX: number;
  cropY: number;
  cropWidth: number;
  cropHeight: number;
};

export enum TClinicPatientType {
  PATIENT = "PATIENT",
  CONSULT = "CONSULT",
  LEAD = "LEAD",
}

export type TAudioFormat = "mp3" | "wav" | "webm" | "m4a" | "ogg";

export type TAudioEncodingConfig = {
  sampleRate: number;
  bitRate: number;
  channels: number;
};

export type TRecordingStatus = "stored" | "uploading" | "uploaded" | "failed";

export type RecordingUploadMode = "single_blob" | "chunked_resumable";

export type SegmentPartStatus = "stored" | "uploading" | "uploaded" | "failed";

export type RemoteUploadSessionStatus = {
  status: string;
  highest_confirmed_part: number;
  missing_part_numbers: number[];
  finalize_ready: boolean;
  final_part_number?: number | null;
  upload_session_id: string;
};

export type TLocalRecording = {
  id: string;
  audioBlob: Blob;
  metadata: {
    patientId: string;
    patientName?: string;
    noteType: string;
    timestamp: string;
    clinicId: string;
    userEmail?: string;
    clinicName?: string;
  };
  status: TRecordingStatus;
  uploadAttempts: number;
  lastAttempt?: string;
};

export type TMP3EncoderResult = {
  blob: Blob;
  originalSize: number;
  encodedSize: number;
  compressionRatio: number;
  encodingTime: number;
};

export type TStorageStats = {
  totalRecordings: number;
  pendingRecordings: number;
  failedRecordings: number;
};

export const BUILTIN_ENCOUNTER_TYPES = [
  "Consultation",
  "Phone Call",
  "Follow Up",
  "Markings",
] as const;

export type TBuiltinEncounterType = (typeof BUILTIN_ENCOUNTER_TYPES)[number];

export type TEncounterType = TBuiltinEncounterType | string;

export type TConsultView =
  | "baseView"
  | "planningView"
  | "presentationView"
  | "consultSettings"
  | "consultNotes";

export type SummaryPromptV1 = {
  kind: "summary_prompt/v1";
  meta?: { label?: string; encounter_type?: string | null };
  system?: { role?: string; style?: string };
  header?: { lines?: string[] }; // tokens: {{date}}, {{patient_name}}, {{encounter_label}}, {{provider_hint}}
  instructions?: { intro?: string; bullets?: string[] };
  sections?: { title: string; bullets?: string[]; body?: string }[];
  transcript_policy?: {
    position?: "append" | "prepend";
    label?: string; // default: "FULL TRANSCRIPT"
    placeholder?: string; // default: "<<<transcription>>>"
  };
  output_policy?: {
    omit_unsupported_content?: boolean;
  };
};

export type TSummaryTemplateData = SummaryPromptV1;

export type TSummaryTemplateResponse = {
  templateData: SummaryPromptV1 | null;
  templateSource: string | null;
  encounterType: TEncounterType | null;

  templateId: number | null;
  userPrompt: string | null; // rendered preview from backend (default path)
  templateJsonVersion: string | null; // e.g., "summary_prompt/v1" | "system+user/v0"
};

export type TSummaryTemplateDefinition = {
  noteType: string;
  encounterType: TEncounterType;
  template: SummaryPromptV1 | string;
};

export type TNoteType =
  | "consultation_summary"
  | "soap_note"
  | "manual_note"
  | "patient_summary";

export type TAudioType =
  | "surgeon_audio"
  | "patient_audio"
  | "patient_coordinator_audio";

export type TSurgeryStageType =
  | "preop"
  | "postop"
  | "intraop"
  | "markings"
  | "wish";
export const SURGERY_STAGE_OPTIONS = [
  "preop",
  "postop",
  "intraop",
  "markings",
  "wish",
] as const;

export type PreSignedUrlPair = {
  put_url: string;
  get_url: string;
};

// Global interface extensions
declare global {
  interface Window {
    stopVoiceNotesAudio?: () => void;
    webkitAudioContext?: typeof AudioContext;
  }

  // Add support for older browsers
  interface Navigator {
    webkitGetUserMedia?: typeof navigator.mediaDevices.getUserMedia;
    mozGetUserMedia?: typeof navigator.mediaDevices.getUserMedia;
  }
}
